<template>
  <div class="venue-item mb-8 border-b border-solid border-gray-400 pb-8 md:flex">
    <div class="mb:pb-0 relative shrink-0 pb-5 md:w-70">
      <VSlickCarousel v-if="venue.photos && venue.photos.length" :arrows="false" :dots="true" class="overflow-hidden rounded">
        <NuxtLink v-for="(photo, idx) in venue.photos" :key="idx" :to="`/${venue.path}`" draggable="false" class="relative block overflow-hidden rounded pb-62%">
          <picture>
            <source v-if="photo.photo_rectangular_webp" :srcset="photo.photo_rectangular_webp" type="image/webp">
            <source :srcset="photo.photo_rectangular" type="image/jpeg">
            <img
              draggable="false"
              class="absolute size-full bg-gray-400 object-cover"
              :src="photo.photo_rectangular"
              :alt="venue.name"
              :loading="idx === 0 ? undefined : 'lazy'"
            >
          </picture>
        </NuxtLink>
      </VSlickCarousel>
      <div class="absolute right-0 top-0 mt-2 block p-2">
        <VenueFavorite :venue="venue" color="#FFFFFF" />
      </div>
    </div>
    <div ref="details" class="w-full md:pl-4">
      <div class="mb-3">
        <NuxtLink class="venue-name relative text-2xl font-bold text-slate" :to="`/${venue.path}`">
          {{ venue.name }}
        </NuxtLink>
      </div>
      <div class="mb-2">
        <a
          href="#"
          class="mr-1 inline-block rounded bg-lime px-2 py-1px text-smm font-medium"
          @click.prevent="$emit('click', venue)"
        >
          {{ Math.floor(venue.tripexpert_score / 10) }}
          <div class="inline-block">
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.5 4.25L7.41406 6.62305L8.625 10.5L5.5 8.09766L2.375 10.5L3.58594 6.62305L0.5 4.25H4.32812L5.5 0.5L6.67188 4.25H10.5Z" fill="#333333" />
            </svg>
          </div>
        </a>
        <div
          v-if="venue.price_category"
          class="mr-1 inline-block rounded bg-gray-6 px-2 py-1px text-smm text-gray-2"
        >
          {{ venue.price_category }}
        </div>
        <div
          v-if="venue.cuisine"
          class="mr-1 inline-block rounded bg-gray-6 px-2 py-1px text-smm text-gray-2"
        >
          {{ venue.cuisine }}
        </div>
        <div
          v-if="venue.neighborhood"
          class="mr-1 inline-block rounded bg-gray-6 px-2 py-1px text-smm text-gray-2"
        >
          {{ venue.neighborhood }}
        </div>
        <div class="mr-1 inline-block rounded bg-gray-6 px-2 py-1px text-smm text-gray-2">
          {{ venue.number_of_reviews }} {{ $f.pluralize('review', venue.number_of_reviews, false) }}
        </div>
      </div>
      <div class="mb-2 leading-snug text-body" v-html="featuredHtml" />
      <div v-if="venue.venue_type_id === 1">
        <div v-if="venue.availability || !filters.check_in" class="flex justify-end">
          <div v-if="venue.low_rate || venue.price" class="pr-4 text-right">
            <div class="pt-1 text-bg font-medium text-gray-1">
              {{ $f.currency(venue.price || venue.low_rate, currency) }}
            </div>
            <a v-if="venue.booking_url" :href="bookingUrl(venue, filters)" target="_blank" rel="noopener noreferrer" class="text-smm text-body hover:underline">on Booking.com</a>
          </div>
          <a
            v-if="venue.booking_url"
            :href="bookingUrl(venue, filters)"
            target="_blank"
            rel="noopener noreferrer"
            class="letter-sp15 mt-2 h-10 rounded-md bg-blue px-8 text-sm uppercase leading-10 text-white hover:bg-blue-dark"
            @click="$gtag.event('book-now')"
          >Book now</a>
        </div>
        <div v-else class="text-right">
          <div class="text-lg font-medium text-body">
            No rooms available
          </div>
          <a href="#" class="text-smm text-body underline" @click.prevent="$eventHub.emit('show-travel-dates-filter')">Try other dates</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { VSlickCarousel } from 'v-slick-carousel'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { mapState } from 'pinia'
import bookingUrl from '~/mixins/booking-url'
import VenueFavorite from '~/components/shared/VenueFavorite'
import { useVenuesStore } from '~/stores/venues'
import { useCurrenciesStore } from '~/stores/currencies'

dayjs.extend(customParseFormat)

export default {
  components: { VenueFavorite, VSlickCarousel },

  mixins: [bookingUrl],

  props: {
    venue: { type: Object, required: true },
    index: { type: Number, required: true },
  },

  emits: ['click'],

  computed: {
    ...mapState(useVenuesStore, ['filters']),
    ...mapState(useCurrenciesStore, ['currency']),

    featuredHtml () {
      if (! this.venue.featured_extract) { return '' }
      const clean = this.venue.featured_extract.replace(/(<([^>]+)>)/ig, '')
      const idx = clean.lastIndexOf('-')

      return clean.substr(0, idx) + (idx === -1 ? '' : `<span class="italic">${clean.substr(idx).replace('-', '—')}</span>`)
    },
  },
}
</script>

<style scoped lang="scss">
:deep() {
  .v-slick-carousel {
    position: relative;
  }

  .v-slick-dots {
    @apply absolute;
    bottom: 14px;

    li {
      margin: 0;

      button {
        &:before {
          color: white;
          opacity: 1;
          font-size: 9px;
        }
      }
    }
  }

  .v-slick-dots li.active button:before {
    @apply text-gray-1 opacity-100;
  }

  .venue-item:hover {
    .venue-name {
      background-image: linear-gradient(to right, black 50%, black 50%);
      background-position: 0 1.3em;
      background-repeat: repeat-x;
      background-size: 8px 3px;
      padding-bottom: 6px;
    }
  }
}
</style>
